import { useEffect, useState } from "react";
import axios from "axios";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { MapPin, Clock, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COAST_IMG = "/images/coast.webp";
const COAST_IMG_FALLBACK = "/images/coast.jpg";

export default function RoutesPrices() {
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/routes`)
      .then((r) => setRoutes(r.data.routes || []))
      .catch(() => setRoutes([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section
      id="preturi"
      data-testid="routes-section"
      className="py-20 lg:py-28 bg-slate-950"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-4">
            <span className="text-amber-600 font-semibold text-sm uppercase tracking-widest">
              Rute & Prețuri
            </span>
            <h2
              data-testid="routes-title"
              className="mt-3 text-3xl lg:text-4xl font-bold text-white"
            >
              Curse populare din Mangalia.
            </h2>
            <p className="mt-5 text-slate-300 leading-relaxed">
              Prețurile afișate sunt orientative pentru o cursă standard (1–4
              persoane, autoturism). Pentru grupuri mai mari — până la{" "}
              <span className="text-white font-semibold">19 persoane</span> cu
              microbuz Mercedes Sprinter — sau rute personalizate, solicită
              ofertă.
            </p>

            <div className="mt-8 rounded-2xl overflow-hidden border border-slate-800 shadow-sm">
              <picture>
                <source srcSet={COAST_IMG} type="image/webp" />
                <img
                  src={COAST_IMG_FALLBACK}
                  alt="Litoralul Mării Negre"
                  className="w-full h-56 object-cover"
                  width="1200"
                  height="600"
                  loading="lazy"
                  decoding="async"
                />
              </picture>
              <div className="bg-slate-900 text-white p-5 flex items-center justify-between">
                <div>
                  <div className="text-xs uppercase tracking-widest text-amber-400">
                    Plecare
                  </div>
                  <div className="font-semibold text-lg">Mangalia</div>
                </div>
                <ArrowRight className="w-5 h-5 text-amber-400" />
                <div className="text-right">
                  <div className="text-xs uppercase tracking-widest text-amber-400">
                    Destinație
                  </div>
                  <div className="font-semibold text-lg">La alegere</div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-8">
            <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-sm">
              <Table data-testid="routes-table">
                <TableHeader>
                  <TableRow className="bg-slate-900 hover:bg-slate-900">
                    <TableHead className="text-amber-400 font-semibold uppercase text-xs tracking-wider">
                      Plecare
                    </TableHead>
                    <TableHead className="text-amber-400 font-semibold uppercase text-xs tracking-wider">
                      Destinație
                    </TableHead>
                    <TableHead className="text-amber-400 font-semibold uppercase text-xs tracking-wider">
                      Durată
                    </TableHead>
                    <TableHead className="text-amber-400 font-semibold uppercase text-xs tracking-wider text-right">
                      Preț
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && (
                    <TableRow>
                      <TableCell
                        colSpan={4}
                        className="text-center py-10 text-slate-400"
                      >
                        Se încarcă rutele...
                      </TableCell>
                    </TableRow>
                  )}
                  {!loading &&
                    routes.map((r, i) => (
                      <TableRow
                        key={`${r.from}-${r.to}`}
                        data-testid={`route-row-${i}`}
                        className="hover:bg-amber-950/40"
                      >
                        <TableCell className="font-medium text-white">
                          <span className="inline-flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-slate-400" />
                            {r.from}
                          </span>
                        </TableCell>
                        <TableCell className="text-slate-200">
                          {r.to}
                        </TableCell>
                        <TableCell className="text-slate-400">
                          <span className="inline-flex items-center gap-1.5">
                            <Clock className="w-3.5 h-3.5" />
                            {r.duration}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="font-semibold text-white text-sm">
                            {r.price}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </div>

            <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-slate-900 text-white rounded-2xl p-6">
              <div>
                <div className="font-semibold text-lg">
                  Nu găsești ruta dorită?
                </div>
                <div className="text-sm text-slate-300">
                  Solicită o ofertă personalizată — răspundem rapid.
                </div>
              </div>
              <Button
                asChild
                data-testid="routes-cta-rezerva"
                className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold rounded-full"
              >
                <a href="#rezervare">Rezervă cursă</a>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
